import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Copy, Eye, KeyRound, Pencil, Plus, ShieldCheck, TerminalSquare, UserCog, X } from 'lucide-react';
import { PERMISSIONS, formatDateTime, type Paginated } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, EmptyState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { EditLocksPanel } from './EditLocksPanel';
import { MailPanel } from './MailPanel';
import { OcrPanel } from './OcrPanel';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select } from '@/components/ui/input';
import { toast } from 'sonner';
import { BackupsPanel } from './BackupsPanel';

interface UserRow {
  id: string; username: string; email: string; full_name: string; status: string; locale: string;
  badge_code: string | null; must_change_password: boolean; last_login_at: string | null;
  created_at: string; roles: string[];
}
interface RoleRow { id: string; code: string; name: string; description: string; permissions: string[] }
interface TerminalRow {
  id: string; code: string; name: string; kind: string; warehouseId: string | null; active: boolean; lastSeenAt: string | null;
}
interface Warehouse { id: string; code: string; name: string }
interface AuditRow {
  id: number; action: string; entity: string; entity_id: string | null; meta: Record<string, unknown> | null;
  ip: string | null; created_at: string; user_name: string | null; username: string | null; terminal_code: string | null;
}

export function AdminPage() {
  const { can, user } = useAuth();
  // El correo saliente lo toca solo el administrador (igual que la API): con `settings.manage`
  // a secas la pestaña saldría con el formulario vacío y guardar borraría la configuración.
  const esAdmin = Boolean(user?.roles.some((role) => role.code === 'admin'));
  const canUsers = can(PERMISSIONS.USERS_MANAGE);
  const canTerminals = can(PERMISSIONS.TERMINALS_MANAGE);
  const canAudit = can(PERMISSIONS.AUDIT_READ);
  const canSettings = can(PERMISSIONS.SETTINGS_MANAGE);
  // `?tab=audit` viene del cuadro de mando: abre la auditoría sin pasar por Usuarios.
  const [searchParams, setSearchParams] = useSearchParams();
  const pedida = searchParams.get('tab');
  const permitidas = [
    ...(canUsers ? ['users'] : []), ...(canTerminals ? ['terminals'] : []),
    ...(canAudit ? ['audit'] : []), ...(canSettings ? ['settings', 'backups', 'ocr'] : []), ...(esAdmin ? ['mail'] : [])
  ];
  const inicial = pedida && permitidas.includes(pedida) ? pedida : permitidas[0] ?? 'settings';
  const clavePermitidas = permitidas.join(',');
  const [tab, setTab] = useState(inicial);
  useEffect(() => {
    const enUrl = searchParams.get('tab');
    if (enUrl && permitidas.includes(enUrl)) setTab(enUrl);
  }, [searchParams, inicial, clavePermitidas]);
  const cambiarTab = (valor: string) => {
    setTab(valor);
    setSearchParams((params) => { params.set('tab', valor); return params; }, { replace: true });
  };

  return (
    <div>
      <PageHeader title="Administración" description="Usuarios, terminales, auditoría y configuración del sistema" />
      <Tabs value={tab} onValueChange={cambiarTab}>
        <TabsList>
          {canUsers ? <TabsTrigger value="users">Usuarios</TabsTrigger> : null}
          {canTerminals ? <TabsTrigger value="terminals">Terminales</TabsTrigger> : null}
          {canAudit ? <TabsTrigger value="audit">Auditoría</TabsTrigger> : null}
          {canSettings ? <TabsTrigger value="settings">Ajustes</TabsTrigger> : null}
          {canSettings ? <TabsTrigger value="backups">Copias</TabsTrigger> : null}
          {canUsers ? <TabsTrigger value="locks">Ediciones abiertas</TabsTrigger> : null}
          {esAdmin ? <TabsTrigger value="mail">Correo</TabsTrigger> : null}
          {canSettings ? <TabsTrigger value="ocr">OCR</TabsTrigger> : null}
        </TabsList>
        {canUsers ? <TabsContent value="users"><UsersPanel currentUserId={user?.id ?? ''} rolesEnabled={canUsers} /></TabsContent> : null}
        {canTerminals ? <TabsContent value="terminals"><TerminalsPanel /></TabsContent> : null}
        {canAudit ? <TabsContent value="audit"><AuditPanel /></TabsContent> : null}
        {canSettings ? <TabsContent value="settings"><SettingsPanel /></TabsContent> : null}
        {canSettings ? <TabsContent value="backups"><BackupsPanel /></TabsContent> : null}
        {canUsers ? <TabsContent value="locks"><EditLocksPanel /></TabsContent> : null}
        {esAdmin ? <TabsContent value="mail"><MailPanel /></TabsContent> : null}
        {canSettings ? <TabsContent value="ocr"><OcrPanel /></TabsContent> : null}
      </Tabs>
    </div>
  );
}

function UsersPanel({ currentUserId }: { currentUserId: string; rolesEnabled: boolean }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  // «Ver como» solo para el administrador, y nunca sobre otro administrador ni sobre uno mismo.
  const canViewAs = can(PERMISSIONS.IMPERSONATE);
  const [viewAs, setViewAs] = useState<UserRow | null>(null);
  const [viewAsTerminal, setViewAsTerminal] = useState('');
  const [viewAsBusy, setViewAsBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Acordeón: el id de la fila abierta (una sola a la vez), no una copia de la fila.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => api<{ data: UserRow[] }>('/users') });
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: () => api<{ data: RoleRow[] }>('/roles') });
  const users = usersQuery.data?.data ?? [];
  const terminalsQuery = useQuery({
    queryKey: ['terminals'],
    queryFn: () => api<{ data: TerminalRow[] }>('/terminals'),
    enabled: canViewAs && Boolean(viewAs)
  });
  // La fila abierta se resuelve contra los datos frescos de la query: tras guardar o
  // refrescar, el formulario ve el usuario actualizado y no una foto vieja.
  const editing = users.find((row) => row.id === editingId) ?? null;

  // Cierra el panel y devuelve el foco al botón que lo abrió: el botón sigue en la
  // tabla, pero al desmontarse el panel el foco caería a <body>.
  const closeEditor = (id: string | null) => {
    setEditingId(null);
    if (id) setTimeout(() => document.getElementById(`edit-btn-${id}`)?.focus(), 0);
  };

  // Si la fila abierta desaparece del listado (borrado, filtro, otro admin), el panel
  // se cierra; sin esto el id quedaría guardado y el panel reaparecería solo.
  useEffect(() => {
    if (!editingId || usersQuery.isLoading) return;
    if (!users.some((row) => row.id === editingId)) setEditingId(null);
  }, [editingId, users, usersQuery.isLoading]);

  const toggleStatus = async (row: UserRow) => {
    try {
      await api(`/users/${row.id}`, { method: 'PATCH', body: { status: row.status === 'active' ? 'inactive' : 'active' } });
      toast.success('Usuario actualizado');
      await qc.invalidateQueries({ queryKey: ['users'] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const enterAs = async (row: UserRow) => {
    setViewAsBusy(true);
    try {
      const result = await api<{ home: string }>('/admin/impersonation', {
        method: 'POST',
        body: { userId: row.id, ...(viewAsTerminal ? { terminalCode: viewAsTerminal } : {}) }
      });
      qc.clear();
      window.location.replace(result.home);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setViewAsBusy(false);
    }
  };

  const resetPassword = async (row: UserRow) => {
    try {
      const result = await api<{ temporaryPassword: string }>(`/users/${row.id}/reset-password`, { method: 'POST', body: {} });
      setTempPassword(result.temporaryPassword);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const columns: Column<UserRow>[] = [
    {
      key: 'user',
      header: 'Usuario',
      cell: (row) => <div><p className="font-medium">{row.full_name}</p><p className="text-[10px] text-muted-foreground">{row.username} · {row.email}</p></div>
    },
    { key: 'roles', header: 'Roles', cell: (row) => <div className="flex flex-wrap gap-1">{row.roles.map((role) => <Badge key={role} tone="outline">{role}</Badge>)}</div> },
    { key: 'badge', header: 'Tarjeta', cell: (row) => <span className="font-mono text-[10px] text-muted-foreground">{row.badge_code ?? '—'}</span> },
    { key: 'status', header: 'Estado', cell: (row) => <StatusBadge status={row.status} /> },
    { key: 'last', header: 'Último acceso', cell: (row) => <span className="text-[10px] text-muted-foreground">{row.last_login_at ? formatDateTime(row.last_login_at) : 'Nunca'}</span> },
    {
      key: 'actions',
      header: '',
      cell: (row) => {
        const open = row.id === editingId;
        return (
          <div className="flex justify-end gap-1">
            <Button
              id={`edit-btn-${row.id}`}
              variant={open ? 'outline' : 'ghost'}
              size="sm"
              aria-expanded={open}
              aria-controls={`edit-user-${row.id}`}
              onClick={() => (open ? closeEditor(row.id) : setEditingId(row.id))}
            >
              <Pencil /> Editar
              <ChevronDown className={cn('transition-transform', open && 'rotate-180')} />
            </Button>
            {canViewAs && row.id !== currentUserId && !row.roles.includes('admin') ? (
              <Button variant="ghost" size="sm" onClick={() => { setViewAs(row); setViewAsTerminal(''); }}>
                <Eye /> Ver como
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => void resetPassword(row)}><KeyRound /> Clave</Button>
            {row.id !== currentUserId ? (
              <Button variant="ghost" size="sm" onClick={() => void toggleStatus(row)}>{row.status === 'active' ? 'Desactivar' : 'Activar'}</Button>
            ) : null}
          </div>
        );
      }
    }
  ];

  if (createOpen) {
    return (
      <CreateUserModal
        open={createOpen} onOpenChange={setCreateOpen} roles={rolesQuery.data?.data ?? []}
        onSaved={() => { setCreateOpen(false); void qc.invalidateQueries({ queryKey: ['users'] }); }}
      />
    );
  }

  return (
    <>
      {tempPassword ? (
        <Card className="mb-4">
          <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
            <div className="flex-1">
              <p className="text-xs font-semibold">Contraseña temporal generada</p>
              <p className="text-[11px] text-muted-foreground">Compártela de forma segura. El usuario deberá cambiarla al entrar.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setTempPassword(null)}>Cerrar</Button>
          </div>
          <div className="flex items-center gap-2 p-3">
            <code className="flex-1 rounded border border-border bg-muted/30 px-3 py-2 font-mono text-sm">{tempPassword}</code>
            <Button variant="outline" size="icon-sm" onClick={() => { if (tempPassword) { void navigator.clipboard.writeText(tempPassword); toast.success('Copiada'); } }}><Copy /></Button>
          </div>
        </Card>
      ) : null}
      {viewAs ? (
        <Card className="mb-4 border-amber-400">
          <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
            <Eye className="size-4 text-amber-600" />
            <div className="flex-1">
              <p className="text-xs font-semibold">Ver la aplicación como {viewAs.full_name}</p>
              <p className="text-[11px] text-muted-foreground">
                Se abre una sesión de <strong>solo lectura</strong> con sus permisos y su puesto, y podrás
                volver a Administración desde la franja de arriba.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setViewAs(null)}>Cancelar</Button>
          </div>
          <div className="flex flex-wrap items-end gap-3 p-3">
            <div className="min-w-56 flex-1">
              <Field label="Terminal">
                <Select value={viewAsTerminal} onChange={(event) => setViewAsTerminal(event.target.value)}>
                  <option value="">El de su puesto (automático)</option>
                  {(terminalsQuery.data?.data ?? [])
                    .filter((terminal) => terminal.active)
                    .map((terminal) => (
                      <option key={terminal.id} value={terminal.code}>{terminal.code} · {terminal.name}</option>
                    ))}
                </Select>
              </Field>
            </div>
            <Button
              disabled={viewAsBusy}
              loading={viewAsBusy}
              onClick={() => void enterAs(viewAs)}
            >
              <Eye /> Entrar como este trabajador
            </Button>
          </div>
        </Card>
      ) : null}
      <Card>
        <div className="flex items-center justify-between border-b border-border p-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><UserCog className="size-4" /> {usersQuery.data?.data.length ?? 0} usuarios</div>
          <Button size="sm" onClick={() => setCreateOpen(true)}><Plus /> Nuevo usuario</Button>
        </div>
        {usersQuery.error ? <ErrorState message={(usersQuery.error as Error).message} onRetry={() => void usersQuery.refetch()} /> : (
          <DataTable
            columns={columns}
            rows={users}
            loading={usersQuery.isLoading}
            rowKey={(row) => row.id}
            empty={<EmptyState title="Sin usuarios" />}
            expandedKey={editing?.id ?? null}
            renderExpanded={(row) => (
              <div
                id={`edit-user-${row.id}`}
                role="region"
                aria-labelledby={`edit-user-title-${row.id}`}
                className="border-t border-border bg-card px-4 py-4"
              >
                <EditUserForm
                  key={row.id}
                  user={row}
                  roles={rolesQuery.data?.data ?? []}
                  rolesError={Boolean(rolesQuery.error)}
                  currentUserId={currentUserId}
                  onClose={() => closeEditor(row.id)}
                  onSaved={() => { closeEditor(row.id); void qc.invalidateQueries({ queryKey: ['users'] }); }}
                />
              </div>
            )}
          />
        )}
      </Card>
    </>
  );
}

function EditUserForm({ user, roles, rolesError, currentUserId, onClose, onSaved }: {
  user: UserRow; roles: RoleRow[]; rolesError: boolean; currentUserId: string;
  onClose: () => void; onSaved: () => void;
}) {
  const isSelf = user.id === currentUserId;
  const formRef = useRef<HTMLDivElement>(null);
  // Foto del usuario al abrir el panel. Los diffs se calculan contra ESTA foto, no contra
  // el prop `user`: el prop se refresca con cada refetch (activar/desactivar otra fila,
  // staleTime, otro administrador) y comparar contra él enviaría como «cambio» lo que
  // hizo otro y lo desharía al guardar.
  const [base] = useState(() => ({
    fullName: user.full_name, email: user.email, locale: user.locale,
    badgeCode: user.badge_code ?? '', status: user.status, roleCodes: user.roles
  }));
  // El componente se monta con `key={user.id}`: al abrir otra fila se reinicia solo.
  const [form, setForm] = useState(base);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // `nearest` solo desplaza si el panel desplegado queda fuera de la ventana.
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const toggleRole = (code: string) => setForm((current) => ({
    ...current,
    roleCodes: current.roleCodes.includes(code) ? current.roleCodes.filter((item) => item !== code) : [...current.roleCodes, code]
  }));

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (!form.fullName.trim() || !form.email.trim() || !form.roleCodes.length) {
        throw new Error('Nombre, email y al menos un rol son obligatorios');
      }
      // Solo se envían los campos que ha cambiado el usuario en el formulario: el diff
      // va contra la foto de apertura, así que lo que cambió fuera no se pisa.
      const patch: Record<string, unknown> = {};
      const fullName = form.fullName.trim();
      const email = form.email.trim();
      const badgeCode = form.badgeCode.trim() || null;
      if (fullName !== base.fullName) patch.fullName = fullName;
      if (email !== base.email) patch.email = email;
      if (form.locale !== base.locale) patch.locale = form.locale;
      if (badgeCode !== (base.badgeCode.trim() || null)) patch.badgeCode = badgeCode;
      const rolesChanged = [...form.roleCodes].sort().join() !== [...base.roleCodes].sort().join();
      if (rolesChanged) patch.roleCodes = form.roleCodes;
      if (!isSelf && form.status !== base.status) patch.status = form.status;
      if (!Object.keys(patch).length) {
        toast('Sin cambios que guardar');
        onClose();
        return;
      }
      await api(`/users/${user.id}`, { method: 'PATCH', body: patch });
      toast.success('Usuario actualizado');
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={formRef} className="animate-fade-in">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={`edit-user-title-${user.id}`} className="text-sm font-semibold text-foreground">Editar {user.username}</h3>
          <p className="text-[11px] text-muted-foreground">Los cambios de roles y de estado se aplican al guardar; desactivar cierra sus sesiones.</p>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Cerrar edición"><X /></Button>
      </div>
      <div className="space-y-3">
        {error ? <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Usuario" hint="No se puede cambiar"><Input value={user.username} readOnly disabled className="font-mono" /></Field>
          <Field label="Nombre completo" required><Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></Field>
          <Field label="Email" required><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Tarjeta QR" hint="Vacío = sin tarjeta"><Input value={form.badgeCode} onChange={(e) => setForm({ ...form, badgeCode: e.target.value })} placeholder="BDG-ALM-02" className="font-mono" /></Field>
          <Field label="Idioma"><Select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}><option value="es">Español</option><option value="en">English</option></Select></Field>
          <Field label="Estado" hint={isSelf ? 'No puedes desactivar tu propio usuario' : undefined}>
            <Select value={form.status} disabled={isSelf} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
              <option value="locked">Bloqueado</option>
            </Select>
          </Field>
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium">Roles</p>
          {isSelf ? <p className="mb-1.5 text-[11px] text-muted-foreground">No puedes quitarte el rol de administrador.</p> : null}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {roles.map((role) => {
              const locked = isSelf && role.code === 'admin';
              return (
                <label key={role.code} className={`flex items-start gap-2 rounded-md border border-border p-2 text-xs ${locked ? 'opacity-60' : ''}`}>
                  <input type="checkbox" checked={form.roleCodes.includes(role.code)} disabled={locked} onChange={() => toggleRole(role.code)} className="mt-0.5" />
                  <span><span className="font-medium">{role.name}</span><span className="block text-[10px] text-muted-foreground">{role.description}</span></span>
                </label>
              );
            })}
          </div>
          {roles.length ? null : (
            <p className="text-[11px] text-muted-foreground">{rolesError ? 'No se pudieron cargar los roles' : 'Cargando roles…'}</p>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button loading={saving} onClick={() => void submit()}>Guardar cambios</Button>
      </div>
    </div>
  );
}

function CreateUserModal({ open, onOpenChange, roles, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; roles: RoleRow[]; onSaved: () => void;
}) {
  const [form, setForm] = useState({ username: '', fullName: '', email: '', password: '', badgeCode: '', locale: 'es', roleCodes: [] as string[] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ username: '', fullName: '', email: '', password: '', badgeCode: '', locale: 'es', roleCodes: [] });
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (!form.username || !form.fullName || !form.email || form.password.length < 10 || !form.roleCodes.length) {
        throw new Error('Completa todos los campos y asigna al menos un rol');
      }
      await api('/users', { method: 'POST', body: { ...form, badgeCode: form.badgeCode || null } });
      toast.success('Usuario creado');
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (code: string) => setForm((current) => ({
    ...current,
    roleCodes: current.roleCodes.includes(code) ? current.roleCodes.filter((item) => item !== code) : [...current.roleCodes, code]
  }));

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="md" title="Nuevo usuario"
      description="El usuario deberá cambiar la contraseña en el primer acceso."
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} onClick={() => void submit()}>Crear usuario</Button></>}
    >
      <div className="space-y-3">
        {error ? <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Usuario" required><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="almacen2" /></Field>
          <Field label="Nombre completo" required><Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></Field>
          <Field label="Email" required><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Contraseña inicial" required hint="Mín. 10, mayúscula, minúscula y número"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <Field label="Tarjeta QR"><Input value={form.badgeCode} onChange={(e) => setForm({ ...form, badgeCode: e.target.value })} placeholder="BDG-ALM-02" className="font-mono" /></Field>
          <Field label="Idioma"><Select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}><option value="es">Español</option><option value="en">English</option></Select></Field>
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium">Roles</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {roles.map((role) => (
              <label key={role.code} className="flex items-start gap-2 rounded-md border border-border p-2 text-xs">
                <input type="checkbox" checked={form.roleCodes.includes(role.code)} onChange={() => toggleRole(role.code)} className="mt-0.5" />
                <span><span className="font-medium">{role.name}</span><span className="block text-[10px] text-muted-foreground">{role.description}</span></span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </InlineView>
  );
}

function TerminalsPanel() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const terminalsQuery = useQuery({ queryKey: ['terminals'], queryFn: () => api<{ data: TerminalRow[] }>('/terminals') });
  const warehousesQuery = useQuery({ queryKey: ['warehouses'], queryFn: () => api<{ data: Warehouse[] }>('/warehouses') });

  const columns: Column<TerminalRow>[] = [
    { key: 'code', header: 'Código', cell: (row) => <span className="font-mono text-xs font-medium">{row.code}</span> },
    { key: 'name', header: 'Nombre', cell: (row) => row.name },
    { key: 'kind', header: 'Tipo', cell: (row) => <Badge tone="outline">{row.kind}</Badge> },
    { key: 'wh', header: 'Almacén', cell: (row) => <span className="text-[11px] text-muted-foreground">{warehousesQuery.data?.data.find((w) => w.id === row.warehouseId)?.name ?? '—'}</span> },
    { key: 'active', header: 'Estado', cell: (row) => <StatusBadge status={row.active ? 'active' : 'inactive'} /> },
    { key: 'seen', header: 'Última actividad', cell: (row) => <span className="text-[10px] text-muted-foreground">{row.lastSeenAt ? formatDateTime(row.lastSeenAt) : '—'}</span> }
  ];

  if (createOpen) {
    return (
      <CreateTerminalModal
        open={createOpen} onOpenChange={setCreateOpen} warehouses={warehousesQuery.data?.data ?? []}
        onSaved={() => { setCreateOpen(false); void qc.invalidateQueries({ queryKey: ['terminals'] }); }}
      />
    );
  }

  return (
    <>
      <Card>
        <div className="flex items-center justify-between border-b border-border p-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><TerminalSquare className="size-4" /> Terminales de fábrica</div>
          <Button size="sm" onClick={() => setCreateOpen(true)}><Plus /> Nuevo terminal</Button>
        </div>
        {terminalsQuery.error ? <ErrorState message={(terminalsQuery.error as Error).message} onRetry={() => void terminalsQuery.refetch()} /> : (
          <DataTable columns={columns} rows={terminalsQuery.data?.data ?? []} loading={terminalsQuery.isLoading} rowKey={(row) => row.id} empty={<EmptyState title="Sin terminales" />} />
        )}
      </Card>
      <CreateTerminalModal
        open={createOpen} onOpenChange={setCreateOpen} warehouses={warehousesQuery.data?.data ?? []}
        onSaved={() => { setCreateOpen(false); void qc.invalidateQueries({ queryKey: ['terminals'] }); }}
      />
    </>
  );
}

function CreateTerminalModal({ open, onOpenChange, warehouses, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; warehouses: Warehouse[]; onSaved: () => void;
}) {
  const [form, setForm] = useState({ code: '', name: '', kind: 'warehouse', warehouseId: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ code: '', name: '', kind: 'warehouse', warehouseId: '' }); }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await api('/terminals', { method: 'POST', body: { ...form, warehouseId: form.warehouseId || null } });
      toast.success('Terminal creado');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView open={open} onOpenChange={onOpenChange} size="sm" title="Nuevo terminal"
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} onClick={() => void submit()}>Crear</Button></>}
    >
      <div className="space-y-3">
        <Field label="Código" required><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="TERMINAL-ALM-03" className="font-mono" /></Field>
        <Field label="Nombre" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Tipo">
          <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="admin">Administración</option><option value="purchasing">Compras</option>
            <option value="warehouse">Almacén</option><option value="packing">Embalaje</option>
            <option value="logistics">Logística</option><option value="management">Dirección</option>
          </Select>
        </Field>
        <Field label="Almacén asociado">
          <Select value={form.warehouseId} onChange={(e) => setForm({ ...form, warehouseId: e.target.value })}>
            <option value="">—</option>
            {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.name}</option>)}
          </Select>
        </Field>
      </div>
    </InlineView>
  );
}

function AuditPanel() {
  const [page, setPage] = useState(1);
  const auditQuery = useQuery({
    queryKey: ['audit', page],
    queryFn: () => api<Paginated<AuditRow>>(`/audit?page=${page}&pageSize=30`)
  });

  const columns: Column<AuditRow>[] = [
    { key: 'date', header: 'Fecha', cell: (row) => <span className="text-[10px] text-muted-foreground">{formatDateTime(row.created_at)}</span> },
    { key: 'action', header: 'Acción', cell: (row) => <Badge tone="outline">{row.action}</Badge> },
    { key: 'entity', header: 'Entidad', cell: (row) => <div><p>{row.entity}</p><p className="font-mono text-[10px] text-muted-foreground">{row.entity_id ?? '—'}</p></div> },
    { key: 'user', header: 'Usuario', cell: (row) => <span className="text-[11px]">{row.user_name ?? 'Sistema'}{row.terminal_code ? <span className="text-muted-foreground"> · {row.terminal_code}</span> : null}</span> },
    { key: 'ip', header: 'IP', cell: (row) => <span className="font-mono text-[10px] text-muted-foreground">{row.ip ?? '—'}</span> },
    { key: 'meta', header: 'Detalle', cell: (row) => <span className="block max-w-[260px] truncate font-mono text-[10px] text-muted-foreground">{row.meta ? JSON.stringify(row.meta) : '—'}</span> }
  ];

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-border p-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="size-4" /> Registro inmutable de operaciones</div>
        <Button variant="outline" size="sm" onClick={() => void auditQuery.refetch()}>Actualizar</Button>
      </div>
      {auditQuery.error ? <ErrorState message={(auditQuery.error as Error).message} onRetry={() => void auditQuery.refetch()} /> : (
        <DataTable
          columns={columns} rows={auditQuery.data?.data ?? []} loading={auditQuery.isLoading} rowKey={(row) => String(row.id)}
          empty={<EmptyState title="Sin registros" />}
          footer={<Pagination page={page} totalPages={auditQuery.data?.meta.totalPages ?? 1} total={auditQuery.data?.meta.total ?? 0} onPageChange={setPage} />}
        />
      )}
    </Card>
  );
}

function SettingsPanel() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ['settings'], queryFn: () => api<{ data: Record<string, unknown> }>('/settings') });
  const [form, setForm] = useState({ companyName: 'Eurobath', horizon: '180', productionLead: '10', allowNegative: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const data = settingsQuery.data?.data;
    if (!data) return;
    setForm({
      companyName: String(data['company.name'] ?? 'Eurobath'),
      horizon: String(data['planning.horizon_days'] ?? 180),
      // Mismo respaldo que el servidor: sin fila, 0. Si aquí pusiera 10, guardar el formulario por
      // cualquier otro motivo escribiría un plazo que nadie ha pedido.
      productionLead: String(data['planning.production_lead_days'] ?? 0),
      allowNegative: Boolean(data['stock.allow_negative'])
    });
  }, [settingsQuery.data]);

  const save = async () => {
    setSaving(true);
    try {
      await api('/settings', {
        method: 'PUT',
        body: {
          'company.name': form.companyName,
          'planning.horizon_days': Number(form.horizon),
          'planning.production_lead_days': Number(form.productionLead),
          'stock.allow_negative': form.allowNegative
        }
      });
      toast.success('Configuración guardada');
      await qc.invalidateQueries({ queryKey: ['settings'] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (settingsQuery.error) return <ErrorState message={(settingsQuery.error as Error).message} onRetry={() => void settingsQuery.refetch()} />;

  return (
    <Card>
      <div className="border-b border-border p-3 text-xs text-muted-foreground">Parámetros globales de la instalación</div>
      <div className="max-w-xl space-y-4 p-4">
        <Field label="Nombre de la empresa"><Input value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Horizonte MRP (días)"><Input type="number" min="7" max="730" value={form.horizon} onChange={(e) => setForm({ ...form, horizon: e.target.value })} /></Field>
          <Field label="Lead time de fabricación (días)"><Input type="number" min="0" value={form.productionLead} onChange={(e) => setForm({ ...form, productionLead: e.target.value })} /></Field>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={form.allowNegative} onChange={(e) => setForm({ ...form, allowNegative: e.target.checked })} />
          Permitir stock negativo (no recomendado)
        </label>
        <Button loading={saving} onClick={() => void save()}>Guardar configuración</Button>
        {/* Atribución obligatoria del catálogo geográfico (CC BY 4.0). */}
        <p className="border-t border-border pt-3 text-[11px] text-muted-foreground">
          Países, provincias y ciudades: <a className="underline" href="https://www.geonames.org" target="_blank" rel="noreferrer">GeoNames</a>,
          licencia CC BY 4.0. Ver <code>packages/db/data/geo/NOTICE.txt</code>.
        </p>
      </div>
    </Card>
  );
}
